import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const INSIGHTS_VIEWPORT_ANCHOR_ID = 'insights-viewport-anchor';

const INSIGHTS_IFRAME_ID = 'az-insights-iframe';
const INSIGHTS_SRC = 'https://graphio.xyz';

/**
 * Mounts a single cross-origin iframe once and positions it over the
 * anchor element in the Insights page when the route is
 * `/insights`. The iframe is never unmounted or reparented, only
 * hidden/shown and repositioned, preserving its internal state.
 */
export const InsightsIframeManager: React.FC = () => {
  const location = useLocation();
  const rafRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    let iframe = document.getElementById(INSIGHTS_IFRAME_ID) as HTMLIFrameElement | null;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = INSIGHTS_IFRAME_ID;
      iframe.src = INSIGHTS_SRC;
      iframe.title = 'Insights';
      iframe.allow = 'fullscreen; clipboard-write';
      iframe.allowFullscreen = true;
      iframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-same-origin');
      iframe.style.position = 'fixed';
      iframe.style.left = '0px';
      iframe.style.top = '0px';
      iframe.style.width = '0px';
      iframe.style.height = '0px';
      iframe.style.border = '0';
      iframe.style.borderRadius = '0.5rem';
      iframe.style.visibility = 'hidden';
      iframe.style.pointerEvents = 'none';
      document.body.appendChild(iframe);
    }

    const updatePosition = () => {
      const anchor = document.getElementById(INSIGHTS_VIEWPORT_ANCHOR_ID);
      if (!anchor || !iframe) return;
      const rect = anchor.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      iframe.style.left = `${Math.round(rect.left)}px`;
      iframe.style.top = `${Math.round(rect.top)}px`;
      iframe.style.width = `${Math.round(rect.width)}px`;
      iframe.style.height = `${Math.round(rect.height)}px`;
    };

    const show = () => {
      if (!iframe) return;
      iframe.style.visibility = 'visible';
      iframe.style.pointerEvents = 'auto';
      updatePosition();
    };

    const hide = () => {
      if (!iframe) return;
      iframe.style.visibility = 'hidden';
      iframe.style.pointerEvents = 'none';
    };

    const onScrollOrResize = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updatePosition);
    };

    const ensureObserved = () => {
      const anchor = document.getElementById(INSIGHTS_VIEWPORT_ANCHOR_ID);
      if (!anchor) return;
      if (resizeObserverRef.current) return;
      resizeObserverRef.current = new ResizeObserver(updatePosition);
      resizeObserverRef.current.observe(anchor);
    };

    const isInsights = location.pathname === '/insights';
    if (isInsights) {
      show();
      ensureObserved();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize, true);
      updatePosition();
      // Also update a moment later in case layout finishes after mount
      setTimeout(updatePosition, 0);
      setTimeout(updatePosition, 200);
    } else {
      hide();
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize, true);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize, true);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [location.pathname]);

  return null;
};
